import { Command } from 'commander';

import { configCommand } from './commands/config.ts';
import { buildOperationCommands, formatOperationList, formatTypeInspection } from './commands/dynamic.ts';
import { loginCommand } from './commands/login.ts';
import { logoutCommand } from './commands/logout.ts';
import { tokenCommand } from './commands/token.ts';
import { useCommand } from './commands/use.ts';
import { whoamiCommand } from './commands/whoami.ts';
import { getIntrospectedSchema, refreshSchema } from './graphql/introspection.ts';
import { error, hint, info } from './lib/output.ts';
import { pkg } from './lib/package-root.ts';
import { captureException, flushSentry, initSentry } from './lib/sentry.ts';
import { checkForUpdates } from './lib/version-check.ts';

initSentry();

// ── Colored help output ──

const useColor = process.stdout.isTTY && !process.env.NO_COLOR;
const ansi = {
  reset: '\x1b[0m',
  bold: '\x1b[1m',
  dim: '\x1b[2m',
  cyan: '\x1b[36m',
  yellow: '\x1b[33m',
  green: '\x1b[32m',
};

function colorizeHelp(text: string): string {
  if (!useColor) return text;
  return text.replace(
    /^(Usage|Options|Commands|Arguments|Quick start|Services|Examples|Queries|Mutations):(.*)$/gm,
    `${ansi.cyan}${ansi.bold}$1:$2${ansi.reset}`,
  );
}

const colorOutput = {
  writeOut: (str: string) => process.stdout.write(colorizeHelp(str)),
  writeErr: (str: string) => process.stderr.write(colorizeHelp(str)),
};

function applyColorToAllCommands(cmd: Command): void {
  cmd.configureOutput(colorOutput);
  for (const sub of cmd.commands) {
    applyColorToAllCommands(sub as Command);
  }
}

const BASE_DESCRIPTION =
  'Sortlist CLI — query and manage the Sortlist platform from your terminal\n\n' +
  'Quick start:\n' +
  '  sl login                    Authenticate with your Sortlist account\n' +
  "  sl whoami                   Check who you're logged in as";

function buildRootDescription(operationCount: number): string {
  const parts = [BASE_DESCRIPTION];

  if (operationCount > 0) {
    parts.push(
      `Every federation operation is a command (${operationCount} available).\n` +
        'Operation names are unique across the supergraph, so they are not namespaced:\n' +
        '  sl operations               List them all\n' +
        '  sl operations --search company\n' +
        '  sl <operation> --help       Flags for one operation',
    );

    parts.push(
      'Examples:\n' +
        '  sl organizations --limit 5\n' +
        '  sl create-application --name continuous-integration\n' +
        '  sl config show',
    );
  }

  return parts.join('\n\n');
}

const program = new Command()
  .name('sl')
  .configureOutput({
    writeOut: (str) => process.stdout.write(colorizeHelp(str)),
    writeErr: (str) => process.stderr.write(colorizeHelp(str)),
  })
  .description(BASE_DESCRIPTION)
  .version(pkg.version)
  .option('--endpoint <url>', 'Override the federation endpoint URL')
  .option('--company <slug|id>', 'Act for this company, by slug or id; must be one you belong to')
  .option('--json', 'Output as JSON (auto-enabled when stdout is not a TTY)')
  .option('--no-color', 'Disable colored output');

// Auth commands (hand-written — interactive flows)
program.addCommand(loginCommand);
program.addCommand(logoutCommand);
program.addCommand(whoamiCommand);
program.addCommand(tokenCommand);
program.addCommand(useCommand);
program.addCommand(configCommand);

// Schema refresh command
const schemaCommand = new Command('schema').description('Manage the cached GraphQL schema');

schemaCommand
  .command('refresh')
  .description('Force-refresh the cached schema from the federation endpoint')
  .action(async function (this: Command) {
    const opts = this.optsWithGlobals();
    try {
      await refreshSchema(opts.endpoint);
      info('Schema refreshed successfully.');
    } catch (err) {
      error(`Failed to refresh schema: ${(err as Error).message}`);
      hint('Check your network connection and endpoint: sl config show');
      process.exit(1);
    }
  });

schemaCommand
  .command('inspect <type>')
  .description('Show the fields/values of a schema type (e.g. an input filter or enum)')
  .action(async function (this: Command, typeName: string) {
    const opts = this.optsWithGlobals();
    try {
      const { schema } = await getIntrospectedSchema(opts.endpoint);
      const formatted = formatTypeInspection(schema, typeName);
      if (!formatted) {
        error(`No type named "${typeName}" in the schema.`);
        hint('Type names are case-sensitive, e.g. `sl schema inspect OrganizationFilterInput`.');
        hint('Find argument types in any command’s --help output.');
        process.exit(1);
      }
      info(formatted);
    } catch (err) {
      error(`Could not read the schema: ${(err as Error).message}`);
      hint('Check your network connection and endpoint: sl config show');
      process.exit(1);
    }
  });

program.addCommand(schemaCommand);

// Parse --endpoint early so introspection uses the right URL
const endpointIdx = process.argv.indexOf('--endpoint');
const endpointOverride = endpointIdx !== -1 ? process.argv[endpointIdx + 1] : undefined;

// Register every federation operation as a top-level command. Hidden from the
// root help — listing 70-odd entries there would bury the curated commands — and
// discoverable through `sl operations`.
try {
  const { schema, fieldServices } = await getIntrospectedSchema(endpointOverride);
  const operations = buildOperationCommands(schema, fieldServices);

  for (const operation of operations) {
    program.addCommand(operation.command, { hidden: true });
  }

  const operationsCommand = new Command('operations')
    .summary('List every federation operation available as a command')
    .option('--search <text>', 'Filter by operation or subgraph name')
    .action(function (this: Command) {
      info(formatOperationList(operations, this.opts().search as string | undefined));
    });

  program.addCommand(operationsCommand);
  program.description(buildRootDescription(operations.length));
} catch (err) {
  // Schema fetch failed — the hand-written commands must still work.
  const message = (err as Error).message;
  const offline = ['login', 'logout', 'config', 'token', 'whoami', 'schema'];
  if (!process.argv.some((a) => offline.includes(a))) {
    error(`Could not fetch schema: ${message}`);
    hint('Check your network connection and endpoint: sl config show');
    hint('You can still use: sl login, sl config, sl schema refresh');
    process.exit(1);
  }
}

applyColorToAllCommands(program);

await checkForUpdates();

try {
  await program.parseAsync(process.argv);
} catch (err) {
  captureException(err);
  await flushSentry();
  throw err;
}

await flushSentry();
